import { httpClient } from "../http-client";
import { NEW_API_ENDPOINTS } from "../../../shared/constants/api";
import type {
  ApiResponseCompanyKpi2Settings,
  ApiResponsePlugin,
  ApiResponsePluginList,
  ApiResponseKpiSlot,
  ApiResponseKpiSlotList,
  ApiResponseKpiReportSummary,
  ApiResponseKpiCalendarCellList,
  ApiResponseKpiReportLogPage,
  ApiResponseFactoryLogPage,
  ApiResponseManualReportResult,
  ApiResponseVoid,
} from "../../../shared/types/api";
import type {
  UpdateCompanyKpi2SettingsRequest,
  CreatePluginRequest,
  UpdatePluginRequest,
  CreateKpiSlotRequest,
  UpdateKpiSlotRequest,
  ManualReportRequest,
} from "../../../domain/types/kpi";
import type { ApiResponse } from "../../../shared/types/api";
import {
  ComputeResponseSchema,
  type ComputeRequest,
  type ComputeResponse,
} from "../../../domain/types/company/company-kpi";

/**
 * KPI 2.3 어드민 API 서비스 (백엔드 AdminKpiPluginController(가입/설정) + AdminKpiController(슬롯/보고), `/api/admin/kpi`).
 *
 * 모든 메서드는 백엔드 ApiResponse<T> 봉투를 그대로 반환한다(response.data 언래핑).
 */
export class AdminKpiApiService {
  // ==================== ① KPI 전역 설정 (SMART_WORKSHOP, pluginId 단위) ====================

  async getSbiz24Settings(
    pluginId: number,
  ): Promise<ApiResponseCompanyKpi2Settings> {
    const response = await httpClient.get<ApiResponseCompanyKpi2Settings>(
      NEW_API_ENDPOINTS.adminKpiController.GET_SBIZ24_SETTINGS(pluginId),
    );
    return response.data;
  }

  async updateSbiz24Settings(
    pluginId: number,
    request: UpdateCompanyKpi2SettingsRequest,
  ): Promise<ApiResponseCompanyKpi2Settings> {
    const response = await httpClient.patch<ApiResponseCompanyKpi2Settings>(
      NEW_API_ENDPOINTS.adminKpiController.PATCH_SBIZ24_SETTINGS(pluginId),
      request,
    );
    return response.data;
  }

  // ==================== ② 가입 사업 = plugins ====================

  async getPluginsByCompany(
    companyId: number,
  ): Promise<ApiResponsePluginList> {
    const response = await httpClient.get<ApiResponsePluginList>(
      NEW_API_ENDPOINTS.adminKpiController.GET_PLUGINS_BY_COMPANY(companyId),
    );
    return response.data;
  }

  async createPlugin(
    request: CreatePluginRequest,
  ): Promise<ApiResponsePlugin> {
    const response = await httpClient.post<ApiResponsePlugin>(
      NEW_API_ENDPOINTS.adminKpiController.POST_PLUGIN,
      request,
    );
    return response.data;
  }

  async updatePlugin(
    pluginId: number,
    request: UpdatePluginRequest,
  ): Promise<ApiResponsePlugin> {
    const response = await httpClient.put<ApiResponsePlugin>(
      NEW_API_ENDPOINTS.adminKpiController.PUT_PLUGIN(pluginId),
      request,
    );
    return response.data;
  }

  async deletePlugin(pluginId: number): Promise<ApiResponseVoid> {
    const response = await httpClient.delete<ApiResponseVoid>(
      NEW_API_ENDPOINTS.adminKpiController.DELETE_PLUGIN(pluginId),
    );
    return response.data;
  }

  /**
   * 대표장비 지정 — PUT /api/admin/kpi/plugin/{pluginId}/representative.
   * 회사(사업)당 1대(company-wide). SMART_WORKSHOP/SMART_FACTORY 동일한 방식.
   * 슬롯/측정 장비 매핑과 무관 — 회사 소속 장비이기만 하면 슬롯이 없어도 지정 가능(먼저 지정 →
   * 이후 슬롯 추가 시 자동 포함되는 순서를 지원).
   */
  async setRepresentativeMachine(
    pluginId: number,
    machineId: number,
  ): Promise<ApiResponseVoid> {
    const response = await httpClient.put<ApiResponseVoid>(
      NEW_API_ENDPOINTS.adminKpiController.PUT_REPRESENTATIVE(pluginId),
      { machineId },
    );
    return response.data;
  }

  // ==================== ③ 슬롯 = sbiz24_config ====================

  async getSlots(pluginId: number): Promise<ApiResponseKpiSlotList> {
    const response = await httpClient.get<ApiResponseKpiSlotList>(
      NEW_API_ENDPOINTS.adminKpiController.GET_SLOTS(pluginId),
    );
    return response.data;
  }

  async createSlot(
    pluginId: number,
    request: CreateKpiSlotRequest,
  ): Promise<ApiResponseKpiSlot> {
    const response = await httpClient.post<ApiResponseKpiSlot>(
      NEW_API_ENDPOINTS.adminKpiController.POST_SLOT(pluginId),
      request,
    );
    return response.data;
  }

  async updateSlot(
    pluginId: number,
    slotId: number,
    request: UpdateKpiSlotRequest,
  ): Promise<ApiResponseKpiSlot> {
    const response = await httpClient.put<ApiResponseKpiSlot>(
      NEW_API_ENDPOINTS.adminKpiController.PUT_SLOT(pluginId, slotId),
      request,
    );
    return response.data;
  }

  async deleteSlot(pluginId: number, slotId: number): Promise<ApiResponseVoid> {
    const response = await httpClient.delete<ApiResponseVoid>(
      NEW_API_ENDPOINTS.adminKpiController.DELETE_SLOT(pluginId, slotId),
    );
    return response.data;
  }

  // ==================== ④ 보고 현황 (요약/캘린더/로그) ====================

  async getReportSummary(
    pluginId: number,
  ): Promise<ApiResponseKpiReportSummary> {
    const response = await httpClient.get<ApiResponseKpiReportSummary>(
      NEW_API_ENDPOINTS.adminKpiController.GET_SUMMARY(pluginId),
    );
    return response.data;
  }

  async getReportCalendar(
    pluginId: number,
    year: number,
    month: number,
    slotId?: number,
  ): Promise<ApiResponseKpiCalendarCellList> {
    const response = await httpClient.get<ApiResponseKpiCalendarCellList>(
      NEW_API_ENDPOINTS.adminKpiController.GET_CALENDAR(
        pluginId,
        year,
        month,
        slotId,
      ),
    );
    return response.data;
  }

  async getReportLogs(
    pluginId: number,
    page?: number,
    size?: number,
  ): Promise<ApiResponseKpiReportLogPage> {
    const response = await httpClient.get<ApiResponseKpiReportLogPage>(
      NEW_API_ENDPOINTS.adminKpiController.GET_LOGS(pluginId, page, size),
    );
    return response.data;
  }

  // ==================== ⑤ 스마트공장 로그 이력 (SMART_FACTORY) ====================

  async getFactoryLogs(
    pluginId: number,
    page?: number,
    size?: number,
  ): Promise<ApiResponseFactoryLogPage> {
    const response = await httpClient.get<ApiResponseFactoryLogPage>(
      NEW_API_ENDPOINTS.adminKpiController.GET_FACTORY_LOGS(pluginId, page, size),
    );
    return response.data;
  }

  // ==================== ⑥ 수동 보고 ====================

  async manualReport(
    pluginId: number,
    request: ManualReportRequest,
  ): Promise<ApiResponseManualReportResult> {
    const response = await httpClient.post<ApiResponseManualReportResult>(
      NEW_API_ENDPOINTS.adminKpiController.POST_MANUAL_REPORT(pluginId),
      request,
    );
    return response.data;
  }

  // ==================== ⑦ 동적 계산 (어드민 그래프) ====================

  /** 특정 회사(companyId)의 KPI 시계열 — 회사 화면과 동일 계산. */
  async compute(companyId: number, body: ComputeRequest): Promise<ComputeResponse> {
    const response = await httpClient.post<ApiResponse<unknown>>(
      NEW_API_ENDPOINTS.adminKpiController.POST_COMPUTE(companyId),
      body,
    );
    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "KPI 그래프 계산에 실패했습니다.");
    }
    return ComputeResponseSchema.parse(response.data.data);
  }
}

export const adminKpiApiService = new AdminKpiApiService();